import type { ReactNode } from 'react'

import { useDisabled, type DisabledState } from './disabled.tsx'
import './segmented.css'

/**
 * 구획 선택기(segmented control) — **알약 하나 안에 선택지가 나란히** 서고 고른 구획이 강조 채움이다.
 * 내 계정 › 개인 설정의 테마 · 테마색 · 언어가 쓴다(eric 2026-09-22 「변경 버튼 빼고 시스템·라이트·다크를 선택하게 —
 * 이것도 하나의 칩인데 세 개 선택 가능하게」). 모니터링의 「장비」 칩과 같은 결이다.
 *
 * 🔴 `role="radiogroup"` + `role="radio"` · `aria-checked` — 색만으로 고른 것을 말하지 않는다.
 * 🔴 `onChange` 를 안 넘기면 **보이기만 하는 선택기**다(언어처럼 고를 것이 하나일 때). 그때 구획은 눌리지 않는다
 *    (`aria-disabled`) — 숨기지 않고 「이것이 지금 값」임을 그대로 보인다.
 */
export interface SegmentedOption<V extends string> {
  value: V
  label: ReactNode
  /**
   * 라벨이 아이콘·색 점 같은 **그림만**일 때의 접근 이름(툴팁도 같은 글자). 글자 라벨이면 넘기지 않는다.
   * eric 2026-09-23 「테마는 아이콘으로 · 테마색은 둥근 원만」 — 눈에는 그림, 보조기술에는 이름.
   */
  title?: string | undefined
}

export type SegmentedProps<V extends string> = {
  /** 접근성 이름(그룹) — 왼쪽 라벨과 같은 말을 넘긴다 */
  label: string
  /** 그룹 id — 제출 검증의 첫 오류 칸 포커스는 이 그룹 안의 구획(고른 것 · 없으면 첫 것)으로 간다 */
  id?: string | undefined
  /** 입력 규칙 위반(아직 안 골랐다 등) — 그룹 테두리가 붉고 굵어진다(`Field.error` 와 같이) */
  invalid?: boolean | undefined
  /** 위반 문구 id(`Field.errorId`) — 그룹과 각 구획이 같이 읽는다 */
  describedBy?: string | undefined
  options: readonly SegmentedOption<V>[]
  value: V
  onChange?: ((value: V) => void) | undefined
} & DisabledState

/**
 * 🔴 **비활성(`disabled` + `disabledReason`)** — 권한 · 상태로 못 고를 때(「추가」 창의 역할 · 판정 · 형태 ·
 *    eric 2026-09-30 「포맷을 모두 동일하게」). `Button` 과 같은 규약이다: 숨기지 않고, 포커스는 살리고, 누름만 막고,
 *    사유는 툴팁 · 보조기술로 낸다(`useDisabled`). 고른 값(`aria-checked`)은 그대로 보인다.
 *    `onChange` 없는 «보이기만 하는» 선택기와는 다르다 — 그쪽은 사유가 없다(고를 것이 하나뿐).
 */
export function Segmented<V extends string>({
  label,
  id,
  invalid,
  describedBy,
  options,
  value,
  onChange,
  disabled,
  disabledReason,
}: SegmentedProps<V>) {
  const { isDisabled, ariaProps, reasonNode } = useDisabled(disabled, disabledReason)
  // 비활성 사유와 위반 문구가 둘 다 있으면 둘 다 읽힌다(`TextInput` 과 같다)
  const description =
    [ariaProps['aria-describedby'], describedBy]
      .filter((v): v is string => typeof v === 'string' && v !== '')
      .join(' ') || undefined
  const fixed = onChange === undefined || isDisabled
  return (
    <div
      aria-describedby={description}
      aria-invalid={invalid === true ? true : undefined}
      aria-label={label}
      className={isDisabled ? 'a-seg a-seg--disabled' : 'a-seg'}
      id={id}
      role="radiogroup"
    >
      {options.map((option) => (
        <button
          aria-checked={option.value === value}
          aria-describedby={description}
          aria-disabled={fixed || undefined}
          {...(option.title === undefined
            ? {}
            : { 'aria-label': option.title, title: option.title })}
          {...(isDisabled ? { title: ariaProps.title } : {})}
          className={option.title === undefined ? 'a-seg__opt' : 'a-seg__opt a-seg__opt--glyph'}
          key={option.value}
          onClick={fixed ? undefined : () => onChange?.(option.value)}
          role="radio"
          type="button"
        >
          {option.label}
        </button>
      ))}
      {reasonNode}
    </div>
  )
}
